import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Settings.css';

function Settings() {
  const navigate = useNavigate();
  const { user, logout, reloadUser } = useAuth();
  
  const [totpSetup, setTotpSetup] = useState(null);
  const [totpToken, setTotpToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSetupTOTP = async () => {
    try {
      setLoading(true);
      setError('');
      const setup = await api.setupTOTP();
      setTotpSetup(setup);
      setLoading(false);
    } catch (err) {
      setError('Failed to setup TOTP');
      setLoading(false);
    }
  };

  const handleEnableTOTP = async (e) => {
    e.preventDefault();
    
    if (!totpToken || totpToken.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await api.enableTOTP(totpSetup.secret, totpToken);
      setSuccess('Two-factor authentication enabled successfully!');
      setTotpSetup(null);
      setTotpToken('');
      await reloadUser();
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
      setLoading(false);
    }
  };

  const handleDisableTOTP = async () => {
    const code = prompt('Enter your current TOTP code to disable two-factor authentication:');
    
    if (!code) return;

    try {
      setLoading(true);
      setError('');
      await api.disableTOTP(code);
      setSuccess('Two-factor authentication disabled successfully!');
      await reloadUser();
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disable TOTP');
      setLoading(false);
    }
  };

  return (
    <div className="settings-page">
      <nav className="navbar">
        <div className="navbar-content">
          <h1 className="navbar-title">Settings</h1>
          <div className="navbar-actions">
            <button className="button button-secondary" onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </button>
            <button className="button button-secondary" onClick={logout}>
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="container">
        {error && <div className="error-banner">{error}</div>}
        {success && <div className="success-banner">{success}</div>}

        {/* User Info */}
        <div className="card">
          <h2 className="section-title">Account Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Email:</span>
              <span className="info-value">{user?.email}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Two-Factor Auth:</span>
              <span className="info-value">
                {user?.totpEnabled ? (
                  <span className="badge badge-success">Enabled</span>
                ) : (
                  <span className="badge badge-warning">Disabled</span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* TOTP Settings */}
        <div className="card">
          <h2 className="section-title">Two-Factor Authentication</h2>
          <p className="section-description">
            Add an extra layer of security to your account by enabling two-factor authentication.
            You'll need an authenticator app like Google Authenticator or Authy.
          </p>

          {!user?.totpEnabled ? (
            <>
              {!totpSetup ? (
                <button
                  className="button button-primary"
                  onClick={handleSetupTOTP}
                  disabled={loading}
                >
                  Enable Two-Factor Authentication
                </button>
              ) : (
                <div className="totp-setup">
                  <div className="setup-step">
                    <h3>Step 1: Scan QR Code</h3>
                    <p>Open your authenticator app and scan this QR code:</p>
                    <div className="qr-code-container">
                      <QRCodeSVG value={totpSetup.manualEntry} size={200} />
                    </div>
                  </div>

                  <div className="setup-step">
                    <h3>Step 2: Manual Entry (Optional)</h3>
                    <p>If you can't scan the QR code, enter this code manually:</p>
                    <div className="manual-code">
                      <code>{totpSetup.secret}</code>
                    </div>
                  </div>

                  <div className="setup-step">
                    <h3>Step 3: Verify Code</h3>
                    <p>Enter the 6-digit code from your authenticator app:</p>
                    <form onSubmit={handleEnableTOTP} className="verify-form">
                      <input
                        type="text"
                        className="input totp-input"
                        value={totpToken}
                        onChange={(e) => setTotpToken(e.target.value.replace(/\D/g, ''))}
                        maxLength="6"
                        placeholder="000000"
                        required
                        autoFocus
                      />
                      <div className="verify-actions">
                        <button
                          type="submit"
                          className="button button-primary"
                          disabled={loading || totpToken.length !== 6}
                        >
                          Verify & Enable
                        </button>
                        <button
                          type="button"
                          className="button button-secondary"
                          onClick={() => {
                            setTotpSetup(null);
                            setTotpToken('');
                            setError('');
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="totp-enabled">
              <p className="totp-status">
                ✓ Two-factor authentication is currently enabled for your account.
              </p>
              <button
                className="button button-danger"
                onClick={handleDisableTOTP}
                disabled={loading}
              >
                Disable Two-Factor Authentication
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Settings;

